import { Button, Color, Flex, InfoTooltip, Modal, Radios, Range, Text, Toggle } from "src/elements";
import { useModalImagineShelvesSettings } from "./store/actions";
import { useEffect, useState } from "react";
import { Tooltip } from "src/components";
import { useLoaders } from "src/components/InlineLoader";
import {
  PayloadPostFixturesGuessing,
  RoutePostFixtureGuessing,
} from "@CommonApi/fixtures-guessing";
import { useModals } from "src/components/Modals";
import { useApi } from "src/api";

const shelfEstimationStrategies = [
  { label: "Conservative", value: "conservative" },
  { label: "Balanced", value: "balanced" },
  { label: "Aggressive", value: "aggressive" },
  { label: "Min DOS only", value: "min_dos_only" },
] satisfies Array<{
  label: string;
  value: PayloadPostFixturesGuessing["configuration"]["shelvesEstimationStrategy"];
}>;

export const ModalImagineShelvesSettings = () => {
  const { imagineShelvesSettingsData, setImagineShelvesStarted, setImagineShelvesSettingsData } =
    useModalImagineShelvesSettings();
  const { hasLoader } = useLoaders();
  const { closeModal } = useModals();
  const { postFixturesGuessingApi } = useApi();

  const [shelvesAlignmentWeight, setShelvesAlignmentWeight] = useState(0.5);
  const [stackToMax, setStackToMaximum] = useState(false);
  const [optimizeOnInitialFixtures, setOptimizeOnInitialFixtures] = useState(false);
  const [shelvesEstimationStrategy, setShelvesEstimationStrategy] = useState(
    shelfEstimationStrategies[1].value,
  );
  const isImagineLoading = hasLoader(`POST_${RoutePostFixtureGuessing}`);

  useEffect(() => {
    return () => {
      setImagineShelvesSettingsData(null);
    };
  }, []);

  useEffect(() => {
    if (imagineShelvesSettingsData?.started) {
      setTimeout(() => {
        closeModal("ModalImagineShelvesSettings");
      }, 2000);
    }
  }, [imagineShelvesSettingsData]);

  if (imagineShelvesSettingsData === null) {
    return null;
  }

  const onClickImagine = async () => {
    await postFixturesGuessingApi({
      categoryId: imagineShelvesSettingsData.categoryId,
      storeIds: imagineShelvesSettingsData.storeIds,
      configuration: {
        optimizeOnInitialFixtures,
        stackToMax,
        shelvesAlignmentWeight,
        shelvesEstimationStrategy,
      },
    });
    setImagineShelvesStarted();
  };

  return (
    <Modal name="ModalImagineShelvesSettings" title="Imagine Shelves" width="700px">
      <Flex
        background={Color.spaceGray}
        padding="20px"
        borderBottom={`1px solid ${Color.spaceGrayHover}`}
      >
        <Text variant="small1" color={Color.white} textAlign="center">
          Say goodbye to manually building and updating fixtures for each assortment and
          merchandising flow revision. Imagine Shelves automatically generates fixtures that best
          fit your latest, store-tailored assortment and flow.
        </Text>
      </Flex>

      <Flex
        column
        justify="center"
        gap="30px"
        padding="20px"
        background={Color.lightGray}
        borderBottom={`1px solid ${Color.lightGrayHover}`}
      >
        <Flex column gap="10px" justify="center">
          <Text variant="body1">Which matters more?</Text>

          <Range
            min={0}
            max={1}
            step={0.01}
            value={shelvesAlignmentWeight}
            setValue={setShelvesAlignmentWeight}
            leftLabel="Assortment Diversity"
            rightLabel="Shelf Alignment"
          />
        </Flex>

        <Flex column justify="center" gap="10px">
          <Flex>
            <Text variant="body1">Stack to maximum?</Text>

            <InfoTooltip
              tooltip={
                <Tooltip width="250px">
                  Enable stacking products to maximum capacity while building potential shelves.
                </Tooltip>
              }
            />
          </Flex>

          <Toggle offLabel="No" onLabel="Yes" value={stackToMax} setValue={setStackToMaximum} />
        </Flex>

        <Flex column justify="center" gap="10px">
          <Text variant="body1">Optimize on initial fixtures?</Text>

          <Toggle
            offLabel="No"
            onLabel="Yes"
            value={optimizeOnInitialFixtures}
            setValue={setOptimizeOnInitialFixtures}
          />
        </Flex>
      </Flex>

      <Flex column padding="20px" gap="20px" justify="center">
        <Radios
          width="auto"
          label="Shelf estimation strategy:"
          tooltip={
            <Tooltip width="300px">
              <Text color={Color.white} whiteSpace="wrap">
                <ul>
                  <li>
                    <b>Conservative</b>: Fewer shelves that fit taller products.
                  </li>
                  <li>
                    <b>Balanced</b>: A balanced number of shelves that support average product
                    heights.
                  </li>
                  <li>
                    <b>Aggressive</b>: More shelves that fit shorter products.
                  </li>
                  <li>
                    <b>Min DOS only</b>: Create potential shelves based on minimum facings required,
                    then re-optimize through multiple iterations until the space is filled.
                  </li>
                </ul>
              </Text>
            </Tooltip>
          }
          options={shelfEstimationStrategies}
          value={shelvesEstimationStrategy}
          setValue={setShelvesEstimationStrategy}
        />

        <Flex>
          <Button
            onClick={onClickImagine}
            isLoading={isImagineLoading}
            isSuccessful={imagineShelvesSettingsData.started}
          >
            Imagine
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
